
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CTAButton from '../../components/base/CTAButton';

export default function CinewallFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Veelgestelde vragen over cinewalls | FAQ';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Antwoorden op veelgestelde vragen over cinewalls kopen, prijzen, techniek en montage.');
    }
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'cinewall vragen, cinewall faq, cinewall informatie, cinewall antwoorden');
    }

    // Add FAQPage Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    // Add LocalBusiness Schema
    const businessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "CINEWALLKOPEN.NL",
      "description": "Specialist in luxe cinewalls op maat met tv, haard en LED-verlichting",
      "url": "https://cinewallkopen.nl",
      "areaServed": {
        "@type": "Country",
        "name": "Nederland"
      },
      "priceRange": "€2500-€7500+",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Cinewall Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Cinewall op maat"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Cinewall met haard"
            }
          }
        ]
      }
    };

    const faqScriptTag = document.createElement('script');
    faqScriptTag.type = 'application/ld+json';
    faqScriptTag.text = JSON.stringify(faqSchema);
    document.head.appendChild(faqScriptTag);

    const businessScriptTag = document.createElement('script');
    businessScriptTag.type = 'application/ld+json';
    businessScriptTag.text = JSON.stringify(businessSchema);
    document.head.appendChild(businessScriptTag);

    window.scrollTo(0, 0);

    return () => {
      document.head.removeChild(faqScriptTag);
      document.head.removeChild(businessScriptTag);
    };
  }, []);

  const faqs = [
    {
      question: 'Wat is een cinewall?',
      answer: 'Een cinewall is een maatwerk wandmeubel waarin televisie, haard, opbergruimte en LED-verlichting worden geïntegreerd tot één luxe en strak geheel. Het is een blikvanger in uw woonkamer die functionaliteit en design combineert.'
    },
    {
      question: 'Wat kost een cinewall op maat?',
      answer: 'De prijs van een cinewall hangt af van afmetingen, materialen en opties. Een basis cinewall zonder haard start vanaf €2.500. Een luxe uitvoering kost tussen €3.500 en €6.000. Met haard en alle opties ligt de prijs tussen €4.000 en €7.500 of meer. Elke cinewall is maatwerk, dus wij adviseren u graag persoonlijk.'
    },
    {
      question: 'Hoe lang duurt het om een cinewall te laten maken?',
      answer: 'Van ontwerp tot montage duurt het gemiddeld 4 tot 6 weken. Dit hangt af van de complexiteit van het ontwerp en de beschikbaarheid van materialen. Wij houden u tijdens het proces op de hoogte van de planning.'
    },
    {
      question: 'Kan ik een cinewall in elke woning plaatsen?',
      answer: 'In principe wel, maar de mogelijkheden hangen af van de beschikbare ruimte, wandconstructie en technische voorzieningen zoals stopcontacten en aansluitingen. Tijdens het intakegesprek beoordelen wij uw situatie en adviseren wij over de beste oplossing.'
    },
    {
      question: 'Wat is het verschil tussen een elektrische en waterdamp haard?',
      answer: 'Een elektrische haard produceert warmte en heeft een gesimuleerd vlameffect. Een waterdamp haard gebruikt waterdamp voor een realistisch vlambeeld zonder warmte. Waterdamp haarden zijn luxer en veiliger voor plaatsing onder een tv, maar ook duurder.'
    },
    {
      question: 'Is het veilig om een tv boven een haard te plaatsen?',
      answer: 'Ja, mits dit technisch correct wordt uitgevoerd. Wij zorgen voor voldoende afstand, ventilatie en isolatie tussen haard en tv. Bij waterdamp haarden is er geen warmte, wat de veiligste optie is. Wij adviseren u over de beste oplossing voor uw situatie.'
    },
    {
      question: 'Welke materialen gebruikt u voor een cinewall?',
      answer: 'Wij werken met hoogwaardige materialen zoals natuursteen, hout, MDF met premium afwerking en metalen accenten. De materiaalkeuze hangt af van uw stijl, budget en wensen. In onze showroom kunt u alle materialen bekijken en voelen.'
    },
    {
      question: 'Kan ik mijn eigen tv en haard laten integreren?',
      answer: 'Ja, dat is mogelijk. Wij adviseren u wel over de juiste afmetingen en technische specificaties om een optimaal resultaat te garanderen. Ook kunnen wij tv en haard voor u regelen als onderdeel van het totaalpakket.'
    },
    {
      question: 'Verzorgen jullie ook de montage?',
      answer: 'Ja, professionele montage is altijd inbegrepen. Onze vakmannen zorgen voor een nette, veilige en technisch correcte plaatsing. Wij laten uw woning netjes achter en testen alle functionaliteiten.'
    },
    {
      question: 'Kan ik de cinewall later aanpassen of uitbreiden?',
      answer: 'Dat hangt af van het ontwerp. Wij denken graag vooruit en adviseren u over toekomstbestendige oplossingen. Verlichting en opbergruimte kunnen vaak later worden aangepast, maar structurele wijzigingen zijn complexer.'
    },
    {
      question: 'Leveren jullie ook buiten Nederland?',
      answer: 'Wij zijn gespecialiseerd in cinewalls in Nederland. Voor projecten net over de grens kunnen wij in overleg kijken naar de mogelijkheden. Neem contact met ons op voor meer informatie.'
    },
    {
      question: 'Kan ik de showroom bezoeken?',
      answer: 'Ja, graag zelfs! In onze showroom kunt u materialen, haarden en verlichting in het echt ervaren. Een showroombezoek is op afspraak en geheel vrijblijvend. Plan uw afspraak via ons contactformulier.'
    },
    {
      question: 'Krijg ik garantie op mijn cinewall?',
      answer: 'Ja, u ontvangt garantie op zowel materiaal als montage. De garantievoorwaarden worden duidelijk vermeld in de offerte. Wij staan voor kwaliteit en vakmanschap.'
    },
    {
      question: 'Hoe onderhoud ik mijn cinewall?',
      answer: 'Een cinewall vraagt weinig onderhoud. Stof regelmatig af met een zachte doek. Voor specifieke materialen zoals natuursteen geven wij u onderhoudsadvies. LED-verlichting gaat jaren mee en is energiezuinig.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-serif text-gray-900 mb-6">
            Veelgestelde vragen
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Antwoorden op de meest gestelde vragen over cinewalls kopen, prijzen, techniek en montage
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-stone-50 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-stone-100 transition-colors cursor-pointer"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-stone-800 flex-shrink-0`}></i>
                </button>
                
                {openIndex === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Questions Section */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-serif text-gray-900 mb-6">
            Staat uw vraag er niet bij?
          </h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            Neem gerust contact met ons op. Wij helpen u graag verder met persoonlijk advies.
          </p>
          <Link to="/cinewall-offerte">
            <CTAButton>
              Stel uw vraag
            </CTAButton>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-stone-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-serif mb-6">
            Klaar om uw cinewall te realiseren?
          </h2>
          <p className="text-xl text-stone-200 mb-10 leading-relaxed">
            Vraag een vrijblijvende offerte aan of plan een showroombezoek
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/cinewall-offerte">
              <CTAButton variant="light">
                Vraag gratis offerte aan
              </CTAButton>
            </Link>
            <Link to="/cinewall-showroom">
              <CTAButton variant="outline-light">
                Plan showroomafspraak
              </CTAButton>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
